/**
 * `.vn` registrant name change ("Đổi tên / chuyển nhượng") and VNNIC panel operations
 * (subsystems/registrar/facade/adapters/admin-api/src/admin/name_changes.rs; spec docs/specs/vn-registrant-name-change.md).
 *
 * A request (NAME_CORRECTION, SUBJECT_CONVERSION + sub-case, HOLDER_TRANSFER) collects the
 * documents of its requirement-set version, is reviewed within 10 days, then the new registrant
 * contact is created at VNNIC and staff enter the change in the VNNIC panel; the domain update
 * poll message completes it. Registry lock / unlock of `.vn` domains use the same panel step.
 *
 * Owner decision 2026-09-29: an organisation renamed while keeping its enterprise code is
 * SUBJECT_CONVERSION / AUTHORITY_RENAME (never NAME_CORRECTION, which staff may only use for an
 * organisation's spelling mistake, with a reason); the enterprise code must match and the
 * declaration under the new name is generated per domain.
 */

import type { CallOptions, ISODateTime, RecordedReason, WriteOptions } from '../types';
import type { DomainAddon } from './services';

export const NAME_CHANGE_CASES = ['HOLDER_TRANSFER', 'SUBJECT_CONVERSION', 'NAME_CORRECTION'] as const;
export type NameChangeCase = (typeof NAME_CHANGE_CASES)[number];
export const NAME_CHANGE_SUB_CASES = ['GIFT_CONTRIBUTION_INHERITANCE', 'AUTHORITY_RENAME', 'DIVISION_MERGER', 'REORGANISATION_SALE'] as const;
export type NameChangeSubCase = (typeof NAME_CHANGE_SUB_CASES)[number];
export const NAME_CHANGE_STATUSES = ['DRAFT', 'SUBMITTED', 'CORRECTION_REQUESTED', 'APPROVED', 'REGISTRY_CONTACT', 'PANEL_PENDING', 'COMPLETED', 'REJECTED', 'CANCELLED'] as const;
export type NameChangeStatus = (typeof NAME_CHANGE_STATUSES)[number];
export type DocumentStage = 'BEFORE_APPROVAL' | 'AFTER_APPROVAL';
/** Where a document comes from: an upload, or the declaration generated for the request's domain. */
export type DocumentSource = 'UPLOAD' | 'DOMAIN_DECLARATION';
export type ReviewDecisionNc = 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTION';
export type ConsentSide = 'CURRENT_REGISTRANT' | 'NEW_REGISTRANT';
export const CONSENT_METHODS = ['IN_PERSON', 'SIGNED_DOCUMENT', 'DIGITAL_SIGNATURE', 'EMAIL_LINK', 'PHONE_CALL'] as const;

/** One document type of a requirement set. */
export interface RequiredDocument {
  code: string;
  name_vi: string;
  name_en: string;
  required: boolean;
  /** Alternatives: any one document of the group satisfies it. */
  one_of: string | null;
  template_ref: string | null;
  signed_by: string | null;
  stage: DocumentStage;
  notes_vi: string | null;
  notes_en: string | null;
  /** Absent / `UPLOAD`: a file; `DOMAIN_DECLARATION`: generated per domain from the new holder's data. */
  source?: DocumentSource | null;
}

export interface RequirementSet {
  id: string;
  case_code: NameChangeCase;
  sub_case: NameChangeSubCase | null;
  version: number;
  effective_from: ISODateTime;
  title_vi: string;
  title_en: string;
  legal_basis: string;
  review_days: number;
  documents: RequiredDocument[];
  reason: string;
  created_by_type: string;
  created_by_id: string;
  created_at: ISODateTime;
}

export interface NewRequirementSet {
  case_code: NameChangeCase;
  sub_case?: NameChangeSubCase | null;
  effective_from?: ISODateTime;
  title_vi: string;
  title_en: string;
  legal_basis: string;
  review_days: number;
  documents: RequiredDocument[];
  reason: string;
}

export interface NameChangeCheck {
  code: string;
  status: 'PASS' | 'FAIL' | 'WARN';
  blocking: boolean;
  detail: string | null;
}

export interface NameChangeRequest {
  id: string;
  domain_id: string;
  case_code: NameChangeCase;
  sub_case: NameChangeSubCase | null;
  requirement_set_id: string;
  status: NameChangeStatus;
  from_subject_id: string | null;
  from_subject_version_id: string | null;
  to_subject_id: string;
  to_subject_version_id: string;
  to_verification_id: string | null;
  consent: Partial<Record<'current_registrant' | 'new_registrant', { method: string; confirmed_at: ISODateTime; actor_type: string; actor_id: string }>>;
  checks: NameChangeCheck[];
  checks_at: ISODateTime | null;
  addon_id: string | null;
  submitted_at: ISODateTime | null;
  review_due_at: ISODateTime | null;
  review_work_item_id: string | null;
  reviewed_by: string | null;
  reviewed_at: ISODateTime | null;
  review_reason: string | null;
  new_contact_handle: string | null;
  /** Organisation holders: the legal representative's contact (ADMIN at VNNIC). */
  new_admin_contact_handle?: string | null;
  contact_created_at: ISODateTime | null;
  panel_operation_id: string | null;
  new_dossier_id: string | null;
  workflow_id: string | null;
  completed_at: ISODateTime | null;
  /** How it completed: the VNNIC poll message, staff ("Xác nhận hoàn tất") or reconciliation. */
  completed_via?: 'POLL' | 'STAFF' | 'RECONCILIATION' | null;
  /** The full client lock applied on completion. */
  completion_lock_status?: 'PENDING' | 'APPLIED' | 'REJECTED' | null;
  completion_lock_at?: ISODateTime | null;
  closed_reason: string | null;
  note: string | null;
  /** An organisation's name correction marked by staff as a spelling correction (why, who). */
  spelling_correction_reason?: string | null;
  spelling_correction_by?: string | null;
  requested_by_type: string;
  requested_by_id: string;
  requested_at: ISODateTime;
  updated_at: ISODateTime;
}

/** Enterprise code of the current registrant vs the new subject version (masked hints only). */
export interface EnterpriseCodeCheck {
  status: 'MATCH' | 'MISMATCH' | 'NOT_APPLICABLE';
  identifier_type: string | null;
  current_hint: string | null;
  new_hint: string | null;
}

/** A declaration under the new name linked to the request (always for exactly its domain). */
export interface NameChangeDeclaration {
  declaration_id: string;
  document_code: string;
  status: 'GENERATED' | 'UPLOADED' | 'VERIFIED' | 'REVIEW_REQUIRED' | 'APPROVED' | 'REJECTED';
  domains: string[];
  template_code: string;
  template_version: string;
  generated_at: ISODateTime;
  attached_at: ISODateTime;
}

/** One `.vn` domain of the current holder, with its latest name-change request. */
export interface HolderDomain {
  domain_id: string;
  domain: string;
  request_id: string | null;
  status: NameChangeStatus | null;
  case_code: NameChangeCase | null;
  sub_case: NameChangeSubCase | null;
  declaration_status: NameChangeDeclaration['status'] | null;
}

export interface NameChangeListItem {
  id: string;
  domain_id: string;
  domain: string;
  case_code: NameChangeCase;
  sub_case: NameChangeSubCase | null;
  status: NameChangeStatus;
  requested_at: ISODateTime;
  submitted_at: ISODateTime | null;
  review_due_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  new_contact_handle: string | null;
  to_holder_name: string;
  from_holder_name: string | null;
  overdue: boolean;
  panel_status: PanelStatus | null;
  requested_by_type: string;
}

export interface NameChangeDocument {
  id: string;
  document_code: string;
  document_id: string;
  document_version_id: string;
  file_name: string | null;
  mime_type: string;
  size_bytes: number;
  uploaded_by_type: string;
  uploaded_at: ISODateTime;
}

export interface NameChangeEvent {
  id: string;
  event_type: string;
  from_status: NameChangeStatus | null;
  to_status: NameChangeStatus | null;
  actor_type: string;
  actor_id: string;
  note: string | null;
  detail: Record<string, unknown>;
  occurred_at: ISODateTime;
}

export type PanelOperationKind = 'NAME_CHANGE' | 'REGISTRY_LOCK' | 'REGISTRY_UNLOCK';
export type PanelStatus = 'PENDING' | 'SUBMITTED' | 'COMPLETED' | 'REFUSED' | 'CANCELLED';

export interface PanelOperation {
  id: string;
  domain_id: string;
  domain?: string | null;
  registry_id: string;
  operation: PanelOperationKind;
  source_type: 'NAME_CHANGE_REQUEST' | 'DOMAIN_ADDON';
  source_id: string;
  executor: 'MANUAL_PANEL' | 'VNNIC_API';
  status: PanelStatus;
  expected: { registrant?: string; statuses_present?: string[]; statuses_absent?: string[] };
  work_item_id: string | null;
  submitted_at: ISODateTime | null;
  submitted_by: string | null;
  submitted_reference: string | null;
  submitted_note: string | null;
  last_checked_at: ISODateTime | null;
  observed: { registrant?: string | null; statuses?: string[]; at?: ISODateTime } | null;
  overdue_flagged_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  closed_reason: RecordedReason | null;
  created_at: ISODateTime;
}

export interface NameChangeChecklistItem extends RequiredDocument {
  present: boolean;
  uploads: number;
}

/** Result of "Xác nhận hoàn tất". */
export interface ConfirmCompletion {
  request: NameChangeRequest;
  registry_registrant: string | null;
  expected_registrant: string | null;
  completed: boolean;
}

export interface NameChangeDetail {
  request: NameChangeRequest;
  domain: string;
  lifecycle_state: string;
  customer_id: string;
  from_holder_name: string | null;
  to_holder_name: string | null;
  requirement_set: { id: string; version: number; latest_version: number; title_vi: string; title_en: string; legal_basis: string; review_days: number; effective_from: ISODateTime };
  checklist: NameChangeChecklistItem[];
  missing_before_approval: string[];
  missing_after_approval: string[];
  documents: NameChangeDocument[];
  timeline: NameChangeEvent[];
  addon: DomainAddon | null;
  panel_operation: PanelOperation | null;
  /** The new holder's subject type. */
  subject_type?: string;
  /** Renames (and an organisation's spelling correction): the enterprise-code comparison. */
  enterprise_code?: EnterpriseCodeCheck | null;
  declarations?: NameChangeDeclaration[];
  holder_domains?: HolderDomain[];
}

export interface NameChangeQuery {
  status?: NameChangeStatus;
  case?: NameChangeCase;
  domain_id?: string;
  open?: boolean;
}

export interface NameChangeApi {
  listNameChanges(query: NameChangeQuery, opts?: CallOptions): Promise<{ items: NameChangeListItem[] }>;
  getNameChange(id: string, opts?: CallOptions): Promise<NameChangeDetail>;
  /**
   * 422 `NAME_CHANGE_CASE_MISMATCH`: an organisation's name correction without a staff spelling
   * mark (or beyond spelling); 422 `ENTERPRISE_CODE_CHANGED`: a rename to another enterprise code.
   */
  openNameChange(
    domainId: string,
    body: { case: NameChangeCase; sub_case?: NameChangeSubCase; to_subject_id: string; note?: string; spelling_correction_reason?: string; reason: string },
    opts?: WriteOptions,
  ): Promise<NameChangeRequest>;
  /** The declaration under the new name for the request's domain: generated, or an existing one linked. */
  nameChangeDeclaration(id: string, body: { declaration_id?: string; reason: string }, opts?: WriteOptions): Promise<{ document_code: string; declaration_id: string; status: NameChangeDeclaration['status']; domains: string[] }>;
  uploadNameChangeDocument(id: string, body: { document_code: string; mime_type: string; file_name?: string; content_base64: string; reason: string }, opts?: WriteOptions): Promise<NameChangeDocument>;
  recordNameChangeConsent(id: string, body: { side: ConsentSide; method: string; reason: string }, opts?: WriteOptions): Promise<{ name_change_id: string; both_confirmed: boolean }>;
  submitNameChange(id: string, body: { reason: string }, opts?: WriteOptions): Promise<NameChangeRequest>;
  reviewNameChange(id: string, body: { decision: ReviewDecisionNc; reason: string }, opts?: WriteOptions): Promise<NameChangeRequest>;
  cancelNameChange(id: string, body: { reason: string }, opts?: WriteOptions): Promise<NameChangeRequest>;
  recordVnnicRefusal(id: string, body: { reason: string }, opts?: WriteOptions): Promise<NameChangeRequest>;
  /**
   * "Xác nhận hoàn tất": reads domain:info; 409 `REGISTRY_NOT_UPDATED` (details
   * `{registry_registrant, expected_registrant}`) while VNNIC shows another registrant.
   */
  confirmNameChangeCompletion(id: string, body: { reason: string }, opts?: WriteOptions): Promise<ConfirmCompletion>;
  listRequirementSets(opts?: CallOptions): Promise<{ items: RequirementSet[]; in_force: string[]; cases: NameChangeCase[]; sub_cases: NameChangeSubCase[] }>;
  publishRequirementSet(body: NewRequirementSet, opts?: WriteOptions): Promise<RequirementSet>;
  listPanelOperations(query: { status?: PanelStatus; domain_id?: string }, opts?: CallOptions): Promise<{ items: PanelOperation[] }>;
  recordPanelSubmitted(id: string, body: { reference?: string; note?: string; reason: string }, opts?: WriteOptions): Promise<PanelOperation>;
  checkPanelOperation(id: string, opts?: WriteOptions): Promise<{ outcome: 'Completed' | 'NotYet' | 'Closed'; operation: PanelOperation | null }>;
  recordPanelRefusal(id: string, body: { reason: string }, opts?: WriteOptions): Promise<PanelOperation>;
}
